/**
 * Two DOM buttons sitting between the opening and its left/right neighbor
 * walls. Clicking one rotates the opening so its `u` axis is parallel to
 * that neighbor's direction (no slide along the wall — orientation only).
 *
 * The buttons are anchored to world points that the controller computes
 * each refresh (`center ± u * (halfWidth + gap)`) and reprojected on
 * every viewer `update` event using the same screen-projection math the
 * context menu uses (`ContextMenu.reprojectToScreen`).
 *
 * Buttons hide automatically when the corresponding neighbor direction
 * is null (dead-end wall) — see `setSideEnabled`.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';

const BUTTON_SIZE_PX = 28;

/**
 * "Parallel lines" icon — two parallel strokes, suggests "align parallel".
 * Inlined SVG so we don't depend on an asset import. Uses currentColor
 * so the button's CSS color drives it.
 */
const PARALLEL_ICON_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
  <line x1="5" y1="6" x2="19" y2="6"/>
  <line x1="5" y1="18" x2="19" y2="18"/>
</svg>`;

/**
 * Side identifier — names the normal half-space rather than left/right
 * because the buttons sit next to the corresponding translate arrow
 * along ±normal. The previous left/right naming was tied to chain
 * termini and didn't survive the redesign where both buttons can come
 * from neighbors at the same terminus.
 */
export type AlignSide = 'pos' | 'neg';

export interface AlignParallelButtonsDeps {
  readonly viewer: Viewer;
  /** Called when the user clicks a button. The controller applies the
   *  rotation, pushes an undo snapshot, and refreshes the gizmos. */
  readonly onClickSide: (side: AlignSide) => void;
}

export class AlignParallelButtons {
  private readonly posButton: HTMLButtonElement;
  private readonly negButton: HTMLButtonElement;
  private posAnchor: THREE.Vector3 | null = null;
  private negAnchor: THREE.Vector3 | null = null;
  private posEnabled = false;
  private negEnabled = false;
  private visible = true;
  private readonly boundReproject: () => void;

  constructor(private readonly deps: AlignParallelButtonsDeps) {
    this.posButton = this.createButton(deps.viewer.translationService.t('Align parallel to neighbor wall (positive-normal side)'));
    this.negButton = this.createButton(deps.viewer.translationService.t('Align parallel to neighbor wall (negative-normal side)'));
    this.posButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.deps.onClickSide('pos');
    });
    this.negButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.deps.onClickSide('neg');
    });
    // Stop mousedown too — without this the canvas mousedown handler
    // would fire on the underlying canvas (the DOM button is layered
    // ABOVE the canvas but the events still bubble through).
    // pointer-events: auto + stopPropagation in the click handler is
    // usually enough, but mousedown is the event the canvas tools listen
    // to first.
    this.posButton.addEventListener('mousedown', (e) => e.stopPropagation());
    this.negButton.addEventListener('mousedown', (e) => e.stopPropagation());
    this.deps.viewer.renderArea.appendChild(this.posButton);
    this.deps.viewer.renderArea.appendChild(this.negButton);
    this.boundReproject = () => this.reproject();
    this.deps.viewer.addEventListener('update', this.boundReproject);
  }

  /**
   * Set the world anchors for both buttons. `null` for a side hides that
   * button (used when no neighbor wall exists on that side).
   */
  setAnchors(pos: THREE.Vector3 | null, neg: THREE.Vector3 | null): void {
    this.posAnchor = pos ? pos.clone() : null;
    this.negAnchor = neg ? neg.clone() : null;
    this.posEnabled = pos !== null;
    this.negEnabled = neg !== null;
    this.reproject();
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.reproject();
  }

  dispose(): void {
    this.deps.viewer.removeEventListener('update', this.boundReproject);
    this.posButton.remove();
    this.negButton.remove();
  }

  private createButton(ariaLabel: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.setAttribute('aria-label', ariaLabel);
    btn.title = ariaLabel;
    btn.innerHTML = PARALLEL_ICON_SVG;
    btn.style.cssText = `
      position: absolute;
      display: none;
      width: ${BUTTON_SIZE_PX}px;
      height: ${BUTTON_SIZE_PX}px;
      padding: 0;
      align-items: center;
      justify-content: center;
      background: rgba(20, 20, 20, 0.92);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      color: #00ff88;
      cursor: pointer;
      z-index: 10000;
      pointer-events: auto;
    `;
    return btn;
  }

  private reproject(): void {
    this.applySideStyle(this.posButton, this.posAnchor, this.posEnabled);
    this.applySideStyle(this.negButton, this.negAnchor, this.negEnabled);
  }

  private applySideStyle(button: HTMLButtonElement, anchor: THREE.Vector3 | null, enabled: boolean): void {
    if (!this.visible || !enabled || !anchor) {
      button.style.display = 'none';
      return;
    }
    const camera = this.deps.viewer.scene.getActiveCamera();
    const size = this.deps.viewer.renderer.getSize(new THREE.Vector2());
    const v = anchor.clone().project(camera);
    // Hide when projection is behind the camera (NDC z out of [-1, 1]).
    if (v.z < -1 || v.z > 1) {
      button.style.display = 'none';
      return;
    }
    const canvasX = (v.x + 1) * 0.5 * size.width;
    const canvasY = (-v.y + 1) * 0.5 * size.height;
    // Center the button on the anchor.
    button.style.display = 'flex';
    button.style.left = `${canvasX - BUTTON_SIZE_PX / 2}px`;
    button.style.top = `${canvasY - BUTTON_SIZE_PX / 2}px`;
  }
}
